import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { csrfToken } from "../utils/csrf"

// The two "add a person" dialogs on /network: the manual form and the paste-a-
// LinkedIn-link form. Native <dialog>s, like the profile's Edit details, so the
// browser owns Escape, the focus trap and the backdrop.
//
// Openers live outside this element (the header's Add menu, the empty state's
// link), so any click on a [data-contact-add-mode] anywhere on the page opens
// the matching dialog. `?add=contact|linkedin` opens one on load, for the
// universal-search quick action and for bookmarks.
//
// Submits go as fetch+JSON: success hands back the new card's URL (the toast
// rides the flash into that page), failure hands back the reason and the
// dialog stays open with the field still filled in.
const MODES = ["contact", "linkedin"]

export default class extends Controller {
    static targets = ["dialog", "error", "submit", "linkedinUrl"]
    static values = { open: String }

    connect() {
        this._onDocumentClick = (event) => {
            const opener = event.target.closest("[data-contact-add-mode]")
            if (!opener) return
            event.preventDefault()
            this.open(opener.dataset.contactAddMode)
        }
        document.addEventListener("click", this._onDocumentClick)
        if (MODES.includes(this.openValue)) {
            this.open(this.openValue)
            this._stripOpenParam()
        }
    }

    disconnect() {
        document.removeEventListener("click", this._onDocumentClick)
    }

    open(mode) {
        const dialog = this._dialog(mode)
        if (!dialog || dialog.open) return

        this.dialogTargets.forEach((other) => { if (other !== dialog && other.open) other.close() })
        this._setError(dialog, null)
        dialog.showModal()
        dialog.querySelector("input:not([type=hidden]), select, textarea")?.focus()
    }

    close(event) {
        event?.preventDefault()
        event?.target.closest("dialog")?.close()
    }

    // A click on the backdrop lands on the <dialog> itself, never on a child.
    backdrop(event) {
        if (event.target === event.currentTarget) event.currentTarget.close()
    }

    // "Type the details instead" on the LinkedIn dialog: carry the pasted link
    // across so it is not typed twice.
    switchToManual(event) {
        event.preventDefault()
        const url = this.hasLinkedinUrlTarget ? this.linkedinUrlTarget.value.trim() : ""
        const manual = this._dialog("contact")
        const field = manual?.querySelector("[name='contact[linkedin_url]']")
        if (field && url) field.value = url
        this.open("contact")
    }

    async submit(event) {
        event.preventDefault()
        const form = event.currentTarget
        const dialog = form.closest("dialog")
        const button = form.querySelector("[type=submit]")
        this._setError(dialog, null)
        if (button) button.disabled = true
        try {
            const response = await fetch(form.action, {
                method: "POST",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
                body: new FormData(form)
            })
            const data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) {
                this._setError(dialog, data.message || "Could not add that contact right now.")
                return
            }
            dialog.close()
            Turbo.visit(data.redirect_url)
        } catch {
            this._setError(dialog, "Could not add that contact right now.")
        } finally {
            if (button) button.disabled = false
        }
    }

    _dialog(mode) {
        return this.dialogTargets.find((dialog) => dialog.dataset.mode === mode)
    }

    _setError(dialog, message) {
        const error = dialog?.querySelector("[data-contact-add-target='error']")
        if (!error) return
        error.textContent = message || ""
        error.hidden = !message
    }

    _stripOpenParam() {
        try {
            const url = new URL(window.location.href)
            url.searchParams.delete("add")
            window.history.replaceState(null, "", url.toString())
        } catch {
            // cosmetic only
        }
    }
}
